import type { FC } from "react"

import ConnectionStatusCell from "./cells/ConnectionStatusCell"
import MemoCell from "./cells/MemoCell"

import {
	type FragmentType,
	getFragmentData,
	graphql,
} from "@/souhl_admin/gql/__generated__"
import type {
	DataSource,
	DataSourcePolicyWithText,
	DataSourceWithText,
} from "@/souhl_admin/gql/__generated__/graphql"

import {
	getPolicyColor,
	getPolicyLabel,
	getSalonStatus,
} from "../../datasourceConfigUtils"
import styles from "../DatasourceConfigTable.module.scss"

const datasourceConfigRowFragment = graphql(/* GraphQL */ `
	fragment DatasourceConfigRow on Salon {
		id
		displayName
		dataSourcePolicy
		goalDataSourcePolicy
		...MemoCell
		dataSourceConnectionStatuses {
			dataSource
			hasIdPassError
			...ConnectionStatusCell
		}
	}
`)

type DatasourceConfigRowProps = {
	salon: FragmentType<typeof datasourceConfigRowFragment>
	dataSourceColumns: DataSourceWithText[]
	policyOptions: readonly DataSourcePolicyWithText[]
	selected: boolean
	onSelectSalon: (salonId: number, checked: boolean) => void
	onUpdateMemo: (salonId: number, memo: string | null) => Promise<void>
	onCopyPassword: (salonId: number, dataSource: DataSource) => Promise<void>
	isPasswordCopying: (salonId: number, dataSource: DataSource) => boolean
}

const DatasourceConfigRow: FC<DatasourceConfigRowProps> = ({
	salon: salonFragment,
	dataSourceColumns,
	policyOptions,
	selected,
	onSelectSalon,
	onUpdateMemo,
	onCopyPassword,
	isPasswordCopying,
}) => {
	const salon = getFragmentData(datasourceConfigRowFragment, salonFragment)
	const status = getSalonStatus(
		salon.dataSourcePolicy,
		salon.goalDataSourcePolicy
	)
	const hasIdPassError = salon.dataSourceConnectionStatuses.some(
		(s) => s.hasIdPassError
	)
	const rowClassName = `${styles.row} ${status === "diff" ? styles.rowDiff : ""} ${hasIdPassError ? styles.rowError : ""}`

	return (
		<tr className={rowClassName}>
			<td className={styles.tdCheckbox}>
				<input
					type="checkbox"
					checked={selected}
					onChange={(e) => onSelectSalon(salon.id, e.target.checked)}
				/>
			</td>
			<td className={styles.tdId}>{salon.id}</td>
			<td className={styles.tdName}>{salon.displayName}</td>
			<td className={styles.tdPolicy}>
				<span
					className={styles.policyBadge}
					style={getPolicyColor(salon.dataSourcePolicy)}
				>
					{getPolicyLabel(salon.dataSourcePolicy, policyOptions)}
				</span>
			</td>
			<td className={styles.tdPolicy}>
				{salon.goalDataSourcePolicy ? (
					<span
						className={styles.policyBadge}
						style={getPolicyColor(salon.goalDataSourcePolicy)}
					>
						{getPolicyLabel(salon.goalDataSourcePolicy, policyOptions)}
					</span>
				) : (
					<span className={styles.noConnection}>--</span>
				)}
			</td>
			<td className={styles.tdDiff}>
				{hasIdPassError && (
					<span className={styles.diffBadgeError}>エラー</span>
				)}
				{!hasIdPassError && status === "match" && (
					<span className={styles.diffBadgeMatch}>一致</span>
				)}
				{!hasIdPassError && status === "diff" && (
					<span className={styles.diffBadgeDiff}>差分あり</span>
				)}
				{!hasIdPassError && status === "unset" && (
					<span className={styles.noConnection}>--</span>
				)}
			</td>
			<td className={styles.tdMemo}>
				<MemoCell salon={salon} onUpdateMemo={onUpdateMemo} />
			</td>
			{dataSourceColumns.length > 0 && (
				<td className={styles.tdSeparator}>
					<div className={styles.separatorLine} />
				</td>
			)}
			{dataSourceColumns.map((col) => {
				const connStatus =
					salon.dataSourceConnectionStatuses.find(
						(s) => s.dataSource === col.dataSource
					) ?? null
				return (
					<td key={col.dataSource} className={styles.tdDataSource}>
						<ConnectionStatusCell
							status={connStatus}
							dataSourceLabel={col.label}
							copying={isPasswordCopying(salon.id, col.dataSource)}
							onCopyPassword={async () =>
								await onCopyPassword(salon.id, col.dataSource)
							}
						/>
					</td>
				)
			})}
		</tr>
	)
}

export default DatasourceConfigRow
